import axios from 'axios';
import saveAs from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import { useAtlasV2MarketShareHooks } from 'src/components/AtlasV2Components/AtlasV2SalesPageComponents/useAtlasV2MarketShareHooks';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { EntityLink } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { BeaconProImageFallback } from 'src/components/SvgIcons';
import { safeDivide } from 'src/utils/app';
import { getPreviousYearWeekId, getYearForWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useCurrentAndLastYearAvailableTimePeriod,
  useMainTimePeriod,
  useMetricFormatter
} from 'src/utils/Hooks';
import { getBrandImageUrl } from 'src/utils/image';

const METRIC_KEY = 'retailSales_sum_value_';
interface RowsDefinition {
  cd: number;
  '1w': number;
  '4w': number;
  '13w': number;
  '26w': number;
  '52w': number;
  ly: number;
  ytd: number;
}

const totalRow = {
  cd: 1,
  '1w': 1,
  '4w': 1,
  '13w': 1,
  '26w': 1,
  '52w': 1,
  ly: 1,
  ytd: 1,
  name: 'totalRow',
  rank: 'totalRow'
};

interface BrandRow {
  brandId: string;
  name: string;
}

export const BrandImage = ({ row, imageMode }: { row: BrandRow; imageMode: 'base64' | 'url' }) => {
  const [imageError, setImageError] = useState(false);
  const [imageData, setImageData] = useState<string>(null);

  useEffect(() => {
    let imageUrl = getBrandImageUrl(row.brandId);
    if (imageMode === 'url') {
      setImageData(imageUrl);
      return;
    }
    imageUrl = `https://stackline-product-images.s3.us-west-2.amazonaws.com/${
      row.brandId
    }.jpg?t=${new Date().getTime()}`;
    axios
      .get(imageUrl, {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
          'accept-language': 'en-US,en;q=0.9,ta;q=0.8',
          'cache-control': 'no-cache',
          pragma: 'no-cache'
        },
        withCredentials: false
      })
      .then((response) => {
        // Convert the array buffer to a binary string
        const binaryStr = Array.from(new Uint8Array(response.data))
          .map((byte) => String.fromCharCode(byte))
          .join('');

        // Base64-encode the binary string
        const base64Data = btoa(binaryStr);

        // Create a data URL from the Base64-encoded string
        const base64Image = `data:image/jpeg;base64,${base64Data}`;
        setImageData(base64Image);
      })
      .catch((error) => {
        console.error('Error fetching the image:', error);
        setImageError(true);
      });
  }, [row.brandId, imageMode]);

  if (imageError) {
    return (
      <BeaconProImageFallback style={{ width: '30px', height: '30px', marginRight: '12px', objectFit: 'cover' }} />
    );
  }

  if (!imageData) {
    return (
      <div
        style={{
          width: '30px',
          height: '30px',
          marginRight: '12px',
          backgroundColor: 'transparent'
        }}
      />
    );
  }

  return (
    <img
      onError={() => setImageError(true)}
      src={imageData}
      style={{ width: '30px', height: '30px', marginRight: '12px', objectFit: 'contain' }}
      alt={row.name}
    />
  );
};

const generateCleanNumber = (value) => (Math.abs(value) < Number.EPSILON ? 0 : value);

const useColumnDefinition = (mainTimePeriodId: string) => {
  const formatMetric = useMetricFormatter();

  const COL_DEFS = [
    {
      headerName: 'Rank',
      field: 'rank',
      valueFormatter: (value) => {
        if (value === 'otherBrandIds' || value === 'totalRow') {
          return <div style={{ fontSize: '14px' }}>-</div>;
        }

        return <div style={{ fontSize: '14px' }}>{value}</div>;
      }
    },
    // Brand Entity
    {
      headerName: 'Brands',
      field: 'name',
      valueFormatter: (value, row) => {
        if (value === 'otherBrandIds') {
          return <div style={{ fontSize: '14px' }}>Other</div>;
        }
        if (value === 'totalRow') {
          return <div style={{ fontSize: '14px' }}>Total</div>;
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BrandImage row={row} imageMode="base64" />
            <EntityLink id={row.brandId} entityType="brand">
              {row.name}
            </EntityLink>
          </div>
        );
      }
    },
    ...(mainTimePeriodId === 'cd'
      ? [
          {
            field: 'cd',
            headerName: 'Custom\n Date',
            valueFormatter: (value) => {
              const cleanNumber = generateCleanNumber(value);
              return (
                <div style={{ fontSize: '14px' }}>
                  {formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}
                </div>
              );
            }
          }
        ]
      : []),
    {
      field: '1w',
      headerName: 'LAST\n WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '4w',
      headerName: 'LAST\n 4 WEEKS',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '13w',
      headerName: 'LAST\n 13 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '26w',
      headerName: 'LAST\n 26 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '52w',
      headerName: 'LAST\n 52 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },
    {
      field: 'ly',
      headerName: 'LAST\n YEAR',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },
    {
      field: 'ytd',
      headerName: 'YTD',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    }
  ];

  return COL_DEFS;
};

/**
 *
 * Market Share grid that will show up in category, brand, subcategory entity in sales page
 */
const AtlasV2MarketShareGrid = () => {
  const { availableMainTimePeriods, id: mainTimePeriodId } = useAppSelector((state) => state.mainTimePeriod);

  const { parsedData, isLoading } = useAtlasV2MarketShareHooks('retailSales');
  const currentAndLastYearTimePeriod = useCurrentAndLastYearAvailableTimePeriod();
  const formatMetric = useMetricFormatter();
  const { startWeek: mainTimeStartWeek, endWeek: mainTimeEndWeek } = useMainTimePeriod();
  const { startWeek: compTimeStartWeek, endWeek: compTimeEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { totalMarketValues, valuesByBrandMap, lastYearMarketValues, lastYearValuesByBrandMap } = parsedData;
  const customWeekIds = [
    `${METRIC_KEY}${mainTimeStartWeek}_${mainTimeEndWeek}`,
    `${METRIC_KEY}${compTimeStartWeek}_${compTimeEndWeek}`
  ];

  const fields = useColumnDefinition(mainTimePeriodId);

  const weekIds: RowsDefinition = useMemo(() => {
    return availableMainTimePeriods
      .filter((tp) => tp.id !== 'mtd')
      .reduce((acc, curr) => {
        acc[curr.id] = [
          `${METRIC_KEY}${curr.startWeek}_${curr.endWeek}`,
          `${METRIC_KEY}${getPreviousYearWeekId(curr.startWeek)}_${getPreviousYearWeekId(curr.endWeek)}`
        ];
        return acc;
      }, {} as RowsDefinition);
  }, [availableMainTimePeriods]);

  const rows = Array.from(valuesByBrandMap)
    .map(([_, metadata], index) => {
      return {
        rank: index + 1,
        brandId: metadata.brandId,
        name: metadata.name,
        cd: safeDivide(metadata[customWeekIds[0]], totalMarketValues[customWeekIds[0]]),
        '1w': safeDivide(metadata[weekIds['1w'][0]], totalMarketValues[weekIds['1w'][0]]),
        '4w': safeDivide(metadata[weekIds['4w'][0]], totalMarketValues[weekIds['4w'][0]]),
        '13w': safeDivide(metadata[weekIds['13w'][0]], totalMarketValues[weekIds['13w'][0]]),
        '26w': safeDivide(metadata[weekIds['26w'][0]], totalMarketValues[weekIds['26w'][0]]),
        '52w': safeDivide(metadata[weekIds['52w'][0]], totalMarketValues[weekIds['52w'][0]]),
        ly: safeDivide(metadata[weekIds.ly[0]], totalMarketValues[weekIds.ly[0]]),
        ytd: safeDivide(metadata[weekIds.ytd[0]], totalMarketValues[weekIds.ytd[0]])
      };
    })
    .filter((row) => row.rank < 11)
    .sort((aRow, bRow) => bRow?.cd - aRow?.cd);

  const salesSumRow = rows.reduce(
    (acc, curr) => {
      acc['1w'] -= curr['1w'];
      acc['4w'] -= curr['4w'];
      acc['13w'] -= curr['13w'];
      acc['26w'] -= curr['26w'];
      acc['52w'] -= curr['52w'];
      acc.cd -= curr.cd;
      acc.ly -= curr.ly;
      acc.ytd -= curr.ytd;
      return acc;
    },
    {
      '1w': 1,
      '4w': 1,
      '13w': 1,
      '26w': 1,
      '52w': 1,
      ly: 1,
      ytd: 1,
      cd: 1,
      name: 'otherBrandIds',
      rank: 'otherBrandIds'
    }
  );

  const convertSeriesToDelimitedData = () => {
    try {
      const header = ['Date Range ->'];
      // fill the header row
      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { startWeek, endWeek } = tpSet[0];
        const { prevYearStartWeek, prevYearEndWeek } = tpSet[1];

        header.push(
          `${getYearForWeekId(+endWeek)} (${startWeek} - ${endWeek})`, // sales
          '', // share
          `${getYearForWeekId(+prevYearEndWeek)} (${prevYearStartWeek} - ${prevYearEndWeek})`, // last year sales
          '', // last year share
          '', // YoY sales
          '' // YoY share
        );
      });

      const subHeader = ['Brand'];
      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { shortDisplayName } = tpSet[0];
        subHeader.push(
          `${shortDisplayName} Sales`,
          `${shortDisplayName} Share`,
          `${shortDisplayName} LY Sales`,
          `${shortDisplayName} LY Share`,
          `${shortDisplayName} YoY Sales (%)`,
          `${shortDisplayName} YoY Share (%)`
        );
      });

      const calculateMetrics = (brand, periodKey) => {
        const currentSales = brand[weekIds[periodKey][0]];
        const lastYearSales = lastYearValuesByBrandMap.get(brand.brandId)[weekIds[periodKey][1]];
        const currentShare = safeDivide(currentSales, totalMarketValues[weekIds[periodKey][0]]);
        const lastYearShare = safeDivide(lastYearSales, lastYearMarketValues[weekIds[periodKey][1]]);
        const yoySales = safeDivide(currentSales, lastYearSales) - 1;
        const yoyShare = safeDivide(currentShare, lastYearShare) - 1;

        return [
          currentSales,
          formatMetric(currentShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          lastYearSales,
          formatMetric(lastYearShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          yoySales,
          formatMetric(yoyShare, METRICTYPE.PERCENT, { decimalPlaces: 2 })
        ];
      };

      const brandRows = Array.from(valuesByBrandMap.values()).map((brand) => {
        const brandRow = [brand.name];
        const periods = ['1w', '4w', '13w', '26w', '52w', 'ly', 'ytd'];

        periods.forEach((periodKey) => {
          brandRow.push(...calculateMetrics(brand, periodKey));
        });

        return brandRow;
      });

      const csvArray = [header, [], subHeader, ...brandRows].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvArray], { type: 'text/plain;charset=utf-8' });
      const fileName = 'market_share_retailSales_data.tsv';
      saveAs(blob, `${fileName}.csv`);
    } catch (error) {
      console.error('Fail to generate - market share grid ', error);
    }

    return Promise.resolve();
  };

  return (
    <>
      <BeaconChartWithLegend
        showTitleSummary
        title="Market Share Grid of Retail Sales"
        convertSeriesToDelimitedData={() => convertSeriesToDelimitedData()}
      >
        <EntityTable
          columnDefs={fields}
          shouldModifyColumnDefs={false}
          showPagination={false}
          isLoading={isLoading}
          rowData={[...rows, salesSumRow, totalRow]}
          skeletonRows={13}
        />
      </BeaconChartWithLegend>
    </>
  );
};

export default AtlasV2MarketShareGrid;
